import { useParams, Link, Navigate } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { ARTICLES, IMAGES } from "@/data/content";

const Article = () => {
  const { slug } = useParams();
  const idx = ARTICLES.findIndex((a) => a.slug === slug);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 26 });

  if (idx === -1) return <Navigate to="/journal" replace />;

  const article = ARTICLES[idx];
  const next = ARTICLES[(idx + 1) % ARTICLES.length];
  let firstPara = true;

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      data-testid="article-page"
      className="pt-16"
    >
      <motion.div
        className="fixed left-0 right-0 top-16 z-[70] h-[2px] origin-left bg-[#CC0A14]"
        style={{ scaleX: progress }}
      />

      <section className="mx-auto max-w-4xl px-5 pt-16 sm:px-8 sm:pt-24">
        <Link
          data-testid="article-back-link"
          to="/journal"
          className="group flex w-fit items-center gap-2 font-jb text-[11px] uppercase tracking-[0.22em] text-zinc-500 transition-colors duration-300 hover:text-white"
        >
          <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
          All articles
        </Link>
        <div className="mt-10 flex items-center gap-4">
          <span className="font-jb text-[10px] uppercase tracking-[0.22em] text-[#CC0A14]">
            {article.category}
          </span>
          <span className="h-3 w-px bg-[#27272A]" />
          <span className="font-jb text-[10px] uppercase tracking-[0.22em] text-zinc-600">
            {article.readTime}
          </span>
        </div>
        <MaskedLines
          lines={[article.title.toUpperCase()]}
          className="mt-6 font-syne text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase leading-[1.04] tracking-tight text-white"
        />
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="mt-6 text-base leading-relaxed text-zinc-400 sm:text-lg"
        >
          {article.excerpt}
        </motion.p>
      </section>

      <Reveal className="mx-auto mt-12 max-w-5xl px-5 sm:px-8">
        <div className="relative border border-[#27272A]">
          <div className="absolute -left-2.5 -top-2.5 z-10 h-6 w-6 border-l-2 border-t-2 border-[#CC0A14]" />
          <div className="relative aspect-[21/9] overflow-hidden">
            <img
              src={IMAGES[article.image]}
              alt={article.title}
              data-testid="article-hero-image"
              className="h-full w-full object-cover grayscale"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#080808]/70 via-transparent to-transparent" />
          </div>
        </div>
      </Reveal>

      <article className="mx-auto max-w-3xl px-5 py-16 sm:px-8 sm:py-20" data-testid="article-body">
        {article.blocks.map((block, bi) => (
          <div key={bi} className="mb-10">
            {block.heading && (
              <h2 className="mb-5 font-syne text-2xl font-bold tracking-tight text-white sm:text-3xl">
                {block.heading}
              </h2>
            )}
            {block.paragraphs.map((p, pi) => {
              const useDropcap = firstPara;
              firstPara = false;
              return (
                <p
                  key={pi}
                  className={`mb-5 text-sm leading-[1.9] text-zinc-300 sm:text-base ${
                    useDropcap ? "dropcap" : ""
                  }`}
                >
                  {p}
                </p>
              );
            })}
          </div>
        ))}
        <div className="mt-14 border border-[#7F1D1D]/40 bg-[#121214] p-7">
          <p className="font-jb text-[10px] uppercase tracking-[0.25em] text-[#CC0A14]">
            Club note
          </p>
          <p className="mt-3 text-sm leading-relaxed text-zinc-400">
            Reading changes nothing by itself. Take the side-profile photo
            today, run the protocol for twelve weeks, then compare. That is the
            entire club.
          </p>
        </div>
      </article>

      <section className="border-t border-[#27272A]">
        <Link
          to={`/journal/${next.slug}`}
          data-testid="article-next-link"
          className="group mx-auto flex max-w-4xl items-center justify-between gap-6 px-5 py-12 sm:px-8"
        >
          <div>
            <p className="font-jb text-[10px] uppercase tracking-[0.25em] text-zinc-600">
              Next article
            </p>
            <p className="mt-3 font-syne text-xl font-bold tracking-tight text-white transition-colors duration-300 group-hover:text-[#CC0A14] sm:text-2xl">
              {next.title}
            </p>
          </div>
          <ArrowRight
            size={26}
            className="shrink-0 text-zinc-600 transition-all duration-300 group-hover:translate-x-1 group-hover:text-[#CC0A14]"
          />
        </Link>
      </section>
    </motion.main>
  );
};

export default Article;
